import React, { useState } from 'react';
import Form from '../../Form/Form';
import { ResidentialCalculatorTitleFeesForm } from '../../../components/Forms';
import {
  TitleFeesOwnersPremiumTable,
  LenderPremiumTable
} from '../../../components/Tables';
import { calculatePremium } from '../../../../lib/helpers';

const ResidentialCalculatorTitleFees = ({
  calculatorVariableConstants,
  calculatorConstants
}) => {
  // destructuring the calculator constants set within the WP admin.
  const {
    simultaneousOwnersTitlePolicy,
    assessmentRecoupmentCharge,
    taxDeletion,
    notDuePayable,
    settlementClosingFee,
    eRecordingFee,
    eRecordingFeeCashDeal,
    estimatedRecordingFee,
    estimatedRecordingFeeCashDeal,
    t_17,
    t_28,
    t_33,
    t_36,
    t_39
  } = calculatorConstants;

  // defining initial input values used as the formProps
  const initialCalculationInputValues = {
    property: '',
    salesPrice: '',
    loanAmount: '',
    whoPaysOwnersPolicy: '',
    whoPaysSurveyAmendmentT1R: '',
    whoPaysT19_1: '',
    surveyAmendmentT1R: '',
    t19_1: ''
  };

  // defining initial calculation results for the Owner premium component
  const initialOwnerCalculationResults = {
    basicPolicy: 0.0,
    assessmentRecoupmentCharge: 0.0,
    surveyAmendmentT1RResult: 0.0,
    t19_1Result: 0.0
  };

  // defining initial calculation results for the title fees lender component
  const initialLenderCalculationResults = {
    simultaneousOwnersTitlePolicy: 0.0,
    assessmentRecoupmentCharge: 0.0,
    taxDeletion: 0.0,
    notDuePayable: 0.0,
    t17LenderResult: 0.0,
    t19LenderResult: 0.0,
    t28LenderResult: 0.0,
    t33LenderResult: 0.0,
    t36LenderResult: 0.0,
    t39LenderResult: 0.0,
    settlementClosingFee: Number(settlementClosingFee),
    eRecordingFee: Number(eRecordingFeeCashDeal),
    estimatedRecordingFee: Number(estimatedRecordingFeeCashDeal)
  };

  // defining the inital checkbox checked state for the lender table component
  const checkboxConstants = {
    lender: {
      t_17_lender: false,
      t_19_lender: false,
      t_28_lender: false,
      t_33_lender: false,
      t_39_lender: false
    }
  };

  // Setting the inital state of the results using the useState hook
  const [ownersTitleResults, setOwnersTitleResults] = useState(
    initialOwnerCalculationResults
  );

  const [lenderTitleResults, setLenderTitleResults] = useState(
    initialLenderCalculationResults
  );

  function calculateLoanPremium(loanAmount) {
    return calculatePremium(loanAmount);
  }

  function surveyDeletion(
    surveyAmendment,
    t19,
    basicPolicy,
    whoPaysSurveyAmendmentT1R
  ) {
    const {
      res_survey_deletion_shared_percentage,
      res_survey_deletion_only_percentage
    } = calculatorVariableConstants;
    let amendment = 0.0;

    if (
      surveyAmendment == 'yes' &&
      whoPaysSurveyAmendmentT1R &&
      whoPaysSurveyAmendmentT1R !== 'seller'
    ) {
      if (t19 == 'yes') {
        amendment = res_survey_deletion_shared_percentage * Number(basicPolicy);
      } else {
        amendment = res_survey_deletion_only_percentage * Number(basicPolicy);
      }

      if (amendment < 20) {
        amendment = 20;
      }
    }

    return amendment;
  }

  function t19_1Check(t19, survey, basic, whoPaysT19_1) {
    const {
      res_t_19_1_shared_percentage,
      res_t_19_1_only_percentage
    } = calculatorVariableConstants;
    let result = 0.0;

    if (t19 == 'yes' && whoPaysT19_1 && whoPaysT19_1 !== 'seller') {
      if (survey == 'yes') {
        result = res_t_19_1_shared_percentage * Number(basic);
      } else {
        result = res_t_19_1_only_percentage * Number(basic);
      }

      if (result < 50) {
        result = 50;
      }
    }

    return result;
  }

  function t19(loanPremium) {
    return loanPremium * 0.05 < 50 ? 50 : loanPremium * 0.05;
  }

  function totalOwnersPremium() {
    const {
      basicPolicy,
      surveyAmendmentT1RResult,
      t19_1Result,
      assessmentRecoupmentCharge
    } = ownersTitleResults;

    return (
      basicPolicy +
      surveyAmendmentT1RResult +
      t19_1Result +
      assessmentRecoupmentCharge
    );
  }

  const totalLoanPolicyPremium = () => {
    const {
      simultaneousOwnersTitlePolicy,
      assessmentRecoupmentCharge,
      taxDeletion,
      notDuePayable,
      t17LenderResult,
      t19LenderResult,
      t28LenderResult,
      t33LenderResult,
      t36LenderResult,
      t39LenderResult
    } = lenderTitleResults;

    return (
      simultaneousOwnersTitlePolicy +
      assessmentRecoupmentCharge +
      taxDeletion +
      notDuePayable +
      t17LenderResult +
      t19LenderResult +
      t28LenderResult +
      t33LenderResult +
      t36LenderResult +
      t39LenderResult
    );
  };

  function totalLenderInsuranceAndServicesFee() {
    const { eRecordingFee, settlementClosingFee } = lenderTitleResults;
    return totalLoanPolicyPremium() + eRecordingFee + settlementClosingFee;
  }

  // Run all the calculations and set the sate of the results accordingly
  function runCalculations({
    salesPrice,
    loanAmount,
    surveyAmendmentT1R,
    t19_1,
    whoPaysOwnersPolicy,
    whoPaysSurveyAmendmentT1R,
    whoPaysT19_1,
    ...values
  }) {
    const loanPremium = calculateLoanPremium(loanAmount.replace(/,/g, ''));
    const basicPolicy = calculateLoanPremium(salesPrice.replace(/,/g, ''));
    const surveyAmendmentT1RResult = surveyDeletion(
      surveyAmendmentT1R,
      t19_1,
      basicPolicy,
      whoPaysSurveyAmendmentT1R
    );
    const t19_1Result = t19_1Check(
      t19_1,
      surveyAmendmentT1R,
      basicPolicy,
      whoPaysT19_1
    );
    const t19Lender = t19(loanPremium);

    setOwnersTitleResults({
      ...ownersTitleResults,
      assessmentRecoupmentCharge:
        !whoPaysOwnersPolicy || whoPaysOwnersPolicy === 'seller'
          ? 0.0
          : Number(assessmentRecoupmentCharge),
      basicPolicy:
        !whoPaysOwnersPolicy || whoPaysOwnersPolicy === 'seller'
          ? 0.0
          : basicPolicy,
      whoPaysOwnersPolicy,
      surveyAmendmentT1RResult,
      t19_1Result
    });

    // only set lender title results if the loan amount is not empty and not 0
    if (loanAmount && loanAmount !== '0') {
      setLenderTitleResults({
        ...lenderTitleResults,
        simultaneousOwnersTitlePolicy: Number(simultaneousOwnersTitlePolicy),
        assessmentRecoupmentCharge: Number(assessmentRecoupmentCharge),
        taxDeletion: Number(taxDeletion),
        notDuePayable: Number(notDuePayable),
        eRecordingFee: Number(eRecordingFee),
        estimatedRecordingFee: Number(estimatedRecordingFee),
        t17LenderResult: 0.0,
        t19LenderResult: 0.0,
        t28LenderResult: 0.0,
        t33LenderResult: 0.0,
        t36LenderResult: Number(t_36),
        t39LenderResult: 0.0
      });
    } else {
      setLenderTitleResults(initialLenderCalculationResults);
    }

    // check if true to reset the state if the user enter new number
    if (t_19_lender.value === 'true') {
      setLenderTitleResults({
        ...lenderTitleResults,
        t19LenderResult: t19Lender
      });
    }
  }

  // Setting the state of the table checkboxes passed up from the tables components
  function setCheckedLenders(status) {
    const {
      t_17_lender,
      t_19_lender,
      t_28_lender,
      t_33_lender,
      t_39_lender
    } = status;
    const {
      t17LenderResult,
      t19LenderResult,
      t28LenderResult,
      t33LenderResult,
      t39LenderResult
    } = initialLenderCalculationResults;
    const loanPremium = calculateLoanPremium(
      loanAmount.value.replace(/,/g, '')
    );
    const t19Lender = t19(loanPremium);

    // setting lenderTitleResults state of all results
    setLenderTitleResults({
      ...lenderTitleResults,
      t17LenderResult: t_17_lender === true ? Number(t_17) : t17LenderResult,
      t19LenderResult: t_19_lender === true ? t19Lender : t19LenderResult,
      t28LenderResult: t_28_lender === true ? Number(t_28) : t28LenderResult,
      t33LenderResult: t_33_lender === true ? Number(t_33) : t33LenderResult,
      t39LenderResult: t_39_lender === true ? Number(t_39) : t39LenderResult
    });
  }

  function handleInputBlur(event, formProps) {
    formProps.handleInputChange(event);
    runCalculations(formProps.values);
  }

  // Overriding Form component handleInputChange functions
  function handleInputChange(event, formProps) {
    formProps.handleInputChange(event);
    formProps.values[event.target.name] = event.target.value;
    runCalculations(formProps.values);
  }

  return (
    <>
      <Form
        className="calc__box calc__box--tabled"
        initialCalculationInputValues={initialCalculationInputValues}
        render={({ formProps }) => (
          <ResidentialCalculatorTitleFeesForm
            formProps={{
              ...formProps,
              handleInputChange: event => handleInputChange(event, formProps)
            }}
          />
        )}
      />
      <TitleFeesOwnersPremiumTable
        title="Residential Owner's Policy Premium"
        calculatorType="residentialTitleFees"
        className="table table--striped"
        ownersTitleResults={ownersTitleResults}
        totals={{ totalOwnersPremium: totalOwnersPremium() }}
      />
      <LenderPremiumTable
        title="Lender’s Title Policy Premium"
        subtitle="Please select applicable fees"
        calculatorType="residentialTitleFees"
        checkboxConstants={checkboxConstants.lender}
        className="table table--striped"
        lenderTitleResults={lenderTitleResults}
        setCheckedLenders={setCheckedLenders}
        totals={{
          totalLoanPolicyPremium: totalLoanPolicyPremium(),
          totalLenderInsuranceAndServicesFee: totalLenderInsuranceAndServicesFee()
        }}
      />
    </>
  );
};

export default ResidentialCalculatorTitleFees;
